(function brightcovePlayer(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.videoPlayers = global.NewHomeSource.videoPlayers || {};
  global.NewHomeSource.videoPlayers.brightcove = global.NewHomeSource.videoPlayers.brightcove || {};

  /**
   * @private
   * triggerOnDemandTracker: dispatch the event trackOnDemandEvent
   *
   * @param {Object} eventInformation object with the event information to be triggered.
   */
  function triggerOnDemandTracker(eventInformation) {
    document.body.dispatchEvent(new CustomEvent('trackOnDemandEvent', {
      detail: {
        data: eventInformation
      }
    }));
  }

  /**
   * triggerEvents: triggers the events with the parameter values.
   * @param {Object} eventParameters object parameter values that will be used on the events.
   */
  function triggerEvents(eventParameters) {
    const { googleAnalyticsEventAction, googleAnalytics4EventName } = eventParameters;
    const events = [];
    if (googleAnalytics4EventName) {
      const googleAnalytics4Event = {
        eventType: 'GA4Event',
        eventName: googleAnalytics4EventName,
        params: {
          video_type: 'Brightcove',
          click_target: 'Video event'
        }
      };
      events.push(googleAnalytics4Event);
    }

    if (googleAnalyticsEventAction) {
      const googleAnalyticsEvent = {
        event: 'GAEvent',
        eventType: 'GAEvent',
        eventCategory: 'Video',
        eventAction: googleAnalyticsEventAction,
        eventLabel: 'Brightcove',
      };
      events.push(googleAnalyticsEvent);
    }

    events.forEach(triggerOnDemandTracker);
  }

  const brightcoveDomain = '//players.brightcove.net/';
  const scriptFile = '_default/index.min.js';
  const playerInformation = {
    account: '689254969001',
    player: 'HkxkXn9mf',
  };
  const videoId = 'video-id';
  const defaultEvents = {
    onEnded: function onEnded() {
      const eventParameters = {
        googleAnalyticsEventAction: 'Completed',
        googleAnalytics4EventName: 'video_completed'
      };
      triggerEvents(eventParameters);
    },
    onPlaying: function onPlaying() { },
    onPaused: function onPaused() {
      const eventParameters = {
        googleAnalyticsEventAction: 'Pause',
        googleAnalytics4EventName: 'video_pause'
      };
      triggerEvents(eventParameters);
    },
  };

  let bcPlayer;
  let eventsHandler;
  let player;

  /**
  * @private
  * brightcoveFilepath: returns the path where the JavaScript for the requested player can be found.
  */
  function brightcoveFilepath() {
    return global.document.location.protocol +
      brightcoveDomain +
      playerInformation.account +
      '/' +
      playerInformation.player +
      scriptFile;
  }

  /**
   * @private
   * loadVideo: loads a new video into the Brightcove Player.
   *
   * @param {object} error with the error information.
   * @param {string} video that is going to be loaded.
   */
  function loadVideo(error, video) {
    bcPlayer.catalog.load(video);
  }

  /**
   * @private
   * setAttribute: sets the value as an attribute according to the playerInformation.
   * @param {string} value property that is going to be set in the DOM.
   */
  function setAttribute(value) {
    player.setAttribute('data-' + value, playerInformation[value]);
  }

  /**
   * @private
   * updateVideo: looks for the given video id from the player information, and loads it.
   */
  function updateVideo() {
    bcPlayer.catalog.getVideo(playerInformation[videoId], loadVideo);
  }

  /**
   * @private
   * onTimeUpdate: Executed each second the video is reproduced.
   */

  function onTimeUpdate() {
    const currentTime = Number(this.currentTime());
    const duration = Number(this.duration());
    const percentViewed = Math.floor((currentTime / duration) * 100);
    const ev = this.isEventViewed;
    // eslint-disable-next-line default-case
    switch (true) {
      case (!ev['75'] && percentViewed === 75): {
        const eventParameters = {
          googleAnalyticsEventAction: '75% Watched',
          googleAnalytics4EventName: 'video_75%'
        };
        triggerEvents(eventParameters);
        ev['75'] = true;
        break;
      }
      case (!ev['50'] && percentViewed === 50): {
        const eventParameters = {
          googleAnalyticsEventAction: '50% Watched',
          googleAnalytics4EventName: 'video_50%'
        };
        triggerEvents(eventParameters);
        ev['50'] = true;
        break;
      }
      case (!ev['25'] && percentViewed === 25): {
        const eventParameters = {
          googleAnalyticsEventAction: '25% Watched',
          googleAnalytics4EventName: 'video_25%'
        };
        triggerEvents(eventParameters);
        ev['25'] = true;
        break;
      }
    }
  }

  /**
   * @private
   * onPlaying: triggers the onPlaying event handler.
   */
  function playingHandler(data) {
    const eventData = data;
    eventData.videoId = playerInformation[videoId];
    eventsHandler.onPlaying(eventData);

    const eventParameters = {
      googleAnalyticsEventAction: 'Play',
    };
    triggerEvents(eventParameters);

    bcPlayer.isEventViewed = {};
    bcPlayer.on('timeupdate', onTimeUpdate);
  }

  function onReady() {
    if (eventsHandler.playOnLoaded) {
      bcPlayer.play();
    }
  }

  /**
  * @private
  * createVideo: loads the requested video on the player.
  */
  function createVideo() {
    if (typeof videojs !== 'function') {
      return;
    }

    Object.keys(playerInformation).forEach(setAttribute);
    bcPlayer = global.bc(player);
    bcPlayer.on('ended', eventsHandler.onEnded);
    bcPlayer.on('play', playingHandler);
    bcPlayer.on('pause', eventsHandler.onPaused);
    bcPlayer.on('ready', onReady);
  }

  /**
  * @private
  * loadBrightcoveScript: loads the Brightcove JavaScript needed for the player.
  */
  function loadBrightcoveScript() {
    if (typeof global.videojs === 'function') {
      createVideo();
      return;
    }

    if (global.define) {
      require(
        ['bc'],
        (brightcoveCallback) => {
          global.bc = brightcoveCallback;
          createVideo();
        }
      );

      return;
    }

    const tag = document.getElementsByTagName('meta')[0];
    const script = document.createElement('script');

    script.async = true;
    script.src = brightcoveFilepath();
    script.type = 'text/javascript';
    script.onload = createVideo;
    tag.parentNode.insertBefore(script, tag);
  }

  /**
  * @constructs BrightcovePlayer
  *
  * @param {HTMLVideoElement} video element where the brightcove player is going to be created.
  * @param {string} source id of the video to be displayed.
  * @param {object} config with the extra information needed by the player.
  */
  function Player(video, source, config) {
    player = global.document.getElementById(video);
    playerInformation[videoId] = source;
    eventsHandler = Object.assign({ }, defaultEvents, config);
    loadBrightcoveScript();
  }

  /**
   * @public
   * createContainer: creates an HTML element needed by the Brightcove JavaScript to create the player.
   */
  Player.createContainer = function createContainer() {
    const videoElement = document.createElement('video');
    videoElement.id = 'videoContainer';
    videoElement.style.width = '100%';
    videoElement.style.height = '100%';
    videoElement.innerHTML = '';
    videoElement.classList = 'video-js BrightcoveExperience BrightcoveExperienceID_4160 bc-player-HkxkXn9mf_default';
    videoElement.setAttribute('data-embed', 'default');
    videoElement.setAttribute('data-application-id', 'true');
    videoElement.setAttribute('controls', 'true');

    return videoElement;
  };

  /**
  * @public
  * setVideo: changes the source of the video to the given value.
  *
  * @param {HTMLVideoElement} video element where the YouTube player is going to be created.
  * @param {string} source new video ID.
  */
  Player.setVideo = function setVideo(video, source) {
    if (source) {
      player = global.document.getElementById(video);
      playerInformation[videoId] = source;
    }

    if (!bcPlayer) {
      loadBrightcoveScript();
      return;
    }

    updateVideo();
  };

  /**
  * @public
  * dispose: destroy the video reference for brightcove.
  *
  */
  Player.dispose = function dispose() {
    try {
      if (bcPlayer) {
        bcPlayer.dispose();
      }
    // eslint-disable-next-line no-empty
    } catch (error) {

    }
  };

  /**
  * @public
  * pause: pauses the brightcove video.
  *
  */
  Player.pause = function pause() {
    try {
      if (player) {
        player.pause();
      }
      // eslint-disable-next-line no-empty
    } catch (error) {

    }
  };

  /**
  * @public
  * play: plays the brightcove video.
  *
  */
  Player.play = function play() {
    try {
      if (player) {
        player.play();
      }
      // eslint-disable-next-line no-empty
    } catch (error) {

    }
  };

  global.NewHomeSource.videoPlayers.brightcove.Player = Player;
}(window));

define("brightcoveVideoPlayer", function(){});

